import * as d3 from "d3";
import * as React from "react";
import { PureComponent } from "react";
import Transition from "react-transition-group/Transition";
import TransitionGroup from "react-transition-group/TransitionGroup";
import { Deployment } from "../domain/deployment";
import { Dimensions } from "../domain/dimension";
import { Organization } from "../domain/organization";
import * as VisUtils from "../lib/VisUtils";
import Violin, { ViolinDatum, ViolinMargin, ViolinPosition } from "./Violin";

const duration = 500;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0,
};

const transitionStyles = {
  entering: { opacity: 1, transitionDuration: "500ms", transitionDelay: "300ms" },
  entered: { opacity: 1, transitionDuration: "500ms", transitionDelay: "300ms" },
  exiting: { opacity: 0, transitionDuration: "300ms", transitionDelay: "0ms" },
  exited: { opacity: 0, transitionDuration: "300ms", transitionDelay: "0ms" },
  unmounted: { opacity: 0, transitionDuration: "300ms", transitionDelay: "0ms" },
};

const Fade = ({ in: inProp, children }: any) => (
  <Transition in={inProp} timeout={duration} appear unmountOnExit>
    {(state: keyof typeof transitionStyles) => (
      <div style={{ ...defaultStyle, ...(transitionStyles[state] || {}) }}>{children}</div>
    )}
  </Transition>
);

const GRID_NUM_COLS = 3;
const GRID_X_SPACING_RATIO = 0.2;

export type ViolinGridMode = "list" | "grid" | "overlay" | "away";

interface ViolinGridProps {
  deployment: Deployment;
  mode: ViolinGridMode;
  organizationsData: Organization[];
  violinHeight: number;
  violinWidth: number;
  dims: Dimensions | any;

  offsetY?: number;
  selectedViolinId?: string;
  highlightedViolinId?: string;

  className?: string;
  violinClassName?: any;
  violinMargin?: ViolinMargin;

  valueFormat?: (n: number) => string;
  showSummaryDimAsOutline?: boolean;
  showRanks?: boolean;
  showRatings?: boolean;
  showDelta?: boolean;
  showLinksToDetailPage?: boolean;
  showAvgValueText?: boolean;
  showAxisTicks?: boolean;
  enableTooltip?: boolean;

  onMouseEnter?: (d: ViolinDatum) => void;
  onMouseLeave?: (d: ViolinDatum) => void;

  suppressTransitions?: any;
}

class ViolinGrid extends PureComponent<ViolinGridProps> {
  ref: null | HTMLDivElement = null;
  refFn = (ref: null | HTMLDivElement) => {
    this.ref = ref;
  };

  violinPosition(i: number): ViolinPosition {
    const { mode, organizationsData, violinHeight, violinWidth, offsetY = 0, selectedViolinId } = this.props;

    switch (mode) {
      case "list":
        return {
          left: 0,
          top: Math.round(i * violinHeight + offsetY),
        };
      case "away": {
        let pivot = VisUtils.findIndex(organizationsData, (d: any) => d.id === selectedViolinId);
        if (pivot == null) {
          pivot = organizationsData.length / 2;
        }
        const away = violinHeight * (i / 2 + 1);
        return {
          left: 0,
          top: i < pivot ? -away : +away + (this.ref ? this.ref.clientHeight : 0),
        };
      }
      case "overlay":
        return {
          left: 0,
          top: Math.round(offsetY),
        };
      case "grid":
        return {
          left: Math.round(violinWidth * (1.0 + GRID_X_SPACING_RATIO) * (i % GRID_NUM_COLS)),
          top: Math.round(violinHeight * Math.floor(i / GRID_NUM_COLS) + offsetY),
        };
    }
  }

  height(): number {
    const { mode, organizationsData, violinHeight } = this.props;
    const count = organizationsData.length;

    switch (mode) {
      case "list":
        return count * violinHeight;
      case "overlay":
        return violinHeight;
      case "grid":
        return violinHeight * Math.ceil(count / GRID_NUM_COLS);
      case "away":
        return 0;
    }
  }

  render() {
    const {
      deployment,
      violinHeight,
      violinWidth,
      violinMargin,
      className = "violin__grid",
      violinClassName,
      selectedViolinId,
      valueFormat,
      showRanks,
      showSummaryDimAsOutline,
      showLinksToDetailPage,
      showRatings,
      showDelta,
      enableTooltip,
      mode,
      suppressTransitions,
      organizationsData,
      onMouseEnter,
      onMouseLeave,
    } = this.props;

    if (violinHeight === 0) {
      return <div />;
    }

    const dims = d3.functor(this.props.dims) as (x: Organization) => any;
    const showAvgValueText = d3.functor(this.props.showAvgValueText) as (x: Organization) => any;
    const showAxisTicks = d3.functor(this.props.showAxisTicks) as (x: Organization) => any;

    const maxTitleLength = mode === "grid" ? 25 : 35;

    const violins = organizationsData.map((dd, i) => (
      <Fade key={dd.id}>
        <Violin
          deployment={deployment}
          organizationDatum={dd}
          position={this.violinPosition(i)}
          width={violinWidth}
          height={violinHeight}
          margin={violinMargin}
          valueFormat={valueFormat}
          className={violinClassName ? violinClassName(dd, i) : undefined}
          selected={dd.id === selectedViolinId}
          rank={showRanks ? i + 1 : undefined}
          dims={dims(dd)}
          showSummaryDimAsOutline={showSummaryDimAsOutline}
          showLinkToDetailPage={showLinksToDetailPage}
          showAvgValueText={showAvgValueText(dd)}
          showRating={showRatings}
          showDelta={showDelta}
          showAxisTicks={showAxisTicks(dd)}
          enableTooltip={enableTooltip}
          maxTitleLength={maxTitleLength}
          suppressTransitions={suppressTransitions}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </Fade>
    ));

    return (
      <div ref={this.refFn}>
        <TransitionGroup className={`violins ${className}`} style={{ height: this.height() }}>
          {violins}
        </TransitionGroup>
      </div>
    );
  }
}

export default ViolinGrid;
